import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import supabase from '../../lib/supabase';
import { useOrgContext } from '../../context/OrgContext';
import { ChevronDown, ChevronUp, PhoneCall, MessageCircle, FileText, Star, Trash2 } from 'lucide-react';

export const ContactDetailPage: React.FC = () => {
  const { id } = useParams();
  const [contact, setContact] = useState<any>(null);
  const [acts, setActs] = useState<any[]>([]);
  const [note, setNote] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [editValues, setEditValues] = useState({
    full_name: '',
    email: '',
    phone: '',
    source: '',
    tags: '',
    vat_number: '',
    client_rank: '',
    company: '',
    company_address: '',
    company_city: '',
    company_zip: '',
    google_maps_url: '',
    prospect_status: '',
    lifecycle_stage: '',
    role: '',
    preferred_language: '',
  });
  const [showMoreFields, setShowMoreFields] = useState(false);
  const { orgId, loading: orgLoading, currentUser } = useOrgContext();
  const canEdit = Boolean(currentUser?.canEdit);

  useEffect(() => {
    if (!orgId) {
      if (!orgLoading) {
        setContact(null);
        setActs([]);
      }
      return;
    }
    let active = true;
    (async () => {
      const c = await supabase
        .from('contacts')
        .select('*')
        .eq('id', id)
        .eq('org_id', orgId)
        .maybeSingle();
      const a = await supabase
        .from('activities')
        .select('*')
        .eq('contact_id', id)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
        .limit(100);
      if (!active) return;
      if (!c.error) setContact(c.data);
      if (!a.error && a.data) setActs(a.data);
    })();
    return () => { active = false; };
  }, [id, orgId, orgLoading]);

  async function addNote() {
    if (!canEdit) return;
    if (!orgId) return;
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    const { error } = await supabase.from('activities').insert([{
      contact_id: id,
      lead_id: null,
      type: 'note',
      channel: 'web',
      content: note,
      created_by: uid || null,
      org_id: orgId,
    }]);
    if (error) return alert(error.message);
    setNote('');
    const a = await supabase
      .from('activities')
      .select('*')
      .eq('contact_id', id)
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (!a.error && a.data) setActs(a.data);
  }

  async function deleteNote(activityId: string) {
    if (!canEdit) return;
    if (!orgId || !id) return;

    if (!confirm('Are you sure you want to delete this note?')) {
      return;
    }

    const { error } = await supabase
      .from('activities')
      .delete()
      .eq('id', activityId)
      .eq('org_id', orgId)
      .eq('contact_id', id);

    if (error) {
      alert(error.message);
      return;
    }

    // Refresh activities
    const a = await supabase
      .from('activities')
      .select('*')
      .eq('contact_id', id)
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (!a.error && a.data) setActs(a.data);
  }

  async function updateContactDetails() {
    if (!canEdit) return;
    if (!orgId || !id) return;
    const tagsArray = editValues.tags
      ? editValues.tags.split(',').map(s => s.trim()).filter(Boolean)
      : null;
      const { error } = await supabase
        .from('contacts')
        .update({
          full_name: editValues.full_name || null,
          email: editValues.email || null,
          phone: editValues.phone || null,
          source: editValues.source || null,
          tags: tagsArray,
          vat_number: editValues.vat_number || null,
          client_rank: editValues.client_rank ? Number(editValues.client_rank) : null,
          company: editValues.company || null,
          company_address: editValues.company_address || null,
          company_city: editValues.company_city || null,
          company_zip: editValues.company_zip || null,
          google_maps_url: editValues.google_maps_url || null,
          prospect_status: editValues.prospect_status || null,
          lifecycle_stage: editValues.lifecycle_stage || 'contact',
          role: editValues.role || null,
          preferred_language: editValues.preferred_language || null,
        })
        .eq('id', id)
        .eq('org_id', orgId);
    if (error) {
      alert(error.message);
      return;
    }
    setShowEdit(false);
    // Refresh contact data
    const refreshed = await supabase
      .from('contacts')
      .select('*')
      .eq('id', id)
      .eq('org_id', orgId)
      .maybeSingle();
    if (!refreshed.error && refreshed.data) {
      setContact(refreshed.data);
    }
    // Also refresh activities to update last_activity_at if needed
    const a = await supabase
      .from('activities')
      .select('*')
      .eq('contact_id', id)
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (!a.error && a.data) setActs(a.data);
  }

  const timeline = useMemo(() => acts.map(a => ({
    id: a.id,
    type: a.type,
    channel: a.channel,
    content: a.content,
    created_at: a.created_at,
    created_by: a.created_by,
  })), [acts]);

  const formatTypeLabel = (type?: string) => {
    if (!type) return 'activity';
    const map: Record<string, string> = {
      whatsapp_inbound: 'WhatsApp (inbound)',
      whatsapp_outbound: 'WhatsApp (outbound)',
      review_request_sent: 'Review request',
      appointment_reminder: 'Appointment reminder',
      note: 'Note',
    };
    return map[type] || type.replace(/_/g, ' ');
  };

  const formatChannelLabel = (channel?: string) => {
    if (!channel) return '—';
    const map: Record<string, string> = {
      automation: 'Automation',
      web: 'CRM',
      whatsapp: 'WhatsApp',
    };
    return map[channel] || channel;
  };

  if (!contact) return <div className="text-gray-900 dark:text-gray-100">Loading…</div>;

  const tagString = Array.isArray(contact.tags) ? contact.tags.join(', ') : '';

  const editButton = canEdit ? (
    <button
      onClick={() => {
        setEditValues({
          full_name: contact.full_name || '',
          email: contact.email || '',
          phone: contact.phone || '',
          source: contact.source || '',
          tags: tagString,
          vat_number: contact.vat_number || '',
          client_rank: contact.client_rank ? String(contact.client_rank) : '',
          company: contact.company || '',
          company_address: contact.company_address || '',
          company_city: contact.company_city || '',
          company_zip: contact.company_zip || '',
          google_maps_url: contact.google_maps_url || '',
          prospect_status: contact.prospect_status || '',
          lifecycle_stage: contact.lifecycle_stage || 'contact',
          role: contact.role || '',
          preferred_language: contact.preferred_language || '',
        });
        setShowEdit(true);
      }}
      className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      Edit
    </button>
  ) : null;

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-[320px_1fr] gap-4 lg:gap-6">
      <div className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md order-2 lg:order-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xl font-semibold mb-1 text-gray-900 dark:text-gray-100">{contact.full_name || '—'}</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mb-1 flex items-center gap-2">
              {contact.email ? <a className="hover:text-gray-900 dark:hover:text-gray-100 text-cyan-600 dark:text-cyan-400" href={`mailto:${contact.email}`}>{contact.email}</a> : '—'}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mb-1 flex items-center gap-2">
              {contact.phone ? <a className="hover:text-gray-900 dark:hover:text-gray-100 text-cyan-600 dark:text-cyan-400" href={`tel:${contact.phone}`}><PhoneCall className="inline w-3 h-3 mr-1" />{contact.phone}</a> : '—'}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm">Source: {contact.source || '—'}</div>
            {contact.company && <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">Azienda: {contact.company}</div>}
            {contact.company_address && (
              <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                Indirizzo: {contact.company_address}
                {contact.company_city && `, ${contact.company_city}`}
                {contact.company_zip && ` ${contact.company_zip}`}
              </div>
            )}
            {contact.google_maps_url && (
              <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                <a href={contact.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300">
                  Apri su Google Maps →
                </a>
              </div>
            )}
            {contact.prospect_status && (
              <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                Status Prospezione: <span className="text-cyan-600 dark:text-cyan-400 font-medium">{contact.prospect_status}</span>
              </div>
            )}
            <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">P.IVA / VAT: {contact.vat_number || '—'}</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mt-1 flex items-center gap-2">
              <span>Rank:</span>
              {contact.client_rank ? (
                <span className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }, (_, idx) => (
                    <Star
                      key={idx}
                      className={`w-3.5 h-3.5 ${idx < (contact.client_rank || 0) ? 'text-amber-500 dark:text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
                      strokeWidth={1.5}
                      fill={idx < (contact.client_rank || 0) ? 'currentColor' : 'none'}
                    />
                  ))}
                </span>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">—</span>
              )}
            </div>
          </div>
          {editButton}
        </div>

        {Array.isArray(contact.tags) && contact.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {contact.tags.map((tag: string) => (
              <span key={tag} className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">{tag}</span>
            ))}
          </div>
        )}

        {showEdit && canEdit && (
          <div className="mt-4 p-4 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Edit details</h3>
              <button onClick={() => setShowEdit(false)} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">Close</button>
            </div>
            <div className="grid gap-3">
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.full_name} onChange={e => setEditValues(v => ({ ...v, full_name: e.target.value }))} placeholder="Full name" />
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.email} onChange={e => setEditValues(v => ({ ...v, email: e.target.value }))} placeholder="Email" />
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.phone} onChange={e => setEditValues(v => ({ ...v, phone: e.target.value }))} placeholder="Phone" />
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.vat_number} onChange={e => setEditValues(v => ({ ...v, vat_number: e.target.value }))} placeholder="Partita IVA / VAT" />
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.source} onChange={e => setEditValues(v => ({ ...v, source: e.target.value }))} placeholder="Source" />
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Customer rank</label>
                <select
                  className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 w-full focus:ring-cyan-500 focus:border-cyan-500"
                  value={editValues.client_rank}
                  onChange={e => setEditValues(v => ({ ...v, client_rank: e.target.value }))}
                >
                  <option value="">Not set</option>
                  {[1, 2, 3, 4, 5].map((val) => (
                    <option key={val} value={String(val)}>{val}</option>
                  ))}
                </select>
              </div>
              <textarea className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-sm focus:ring-cyan-500 focus:border-cyan-500" rows={2} value={editValues.tags} onChange={e => setEditValues(v => ({ ...v, tags: e.target.value }))} placeholder="Tags (comma separated)" />
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.company} onChange={e => setEditValues(v => ({ ...v, company: e.target.value }))} placeholder="Azienda" />
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.company_address} onChange={e => setEditValues(v => ({ ...v, company_address: e.target.value }))} placeholder="Indirizzo" />
              <div className="grid grid-cols-2 gap-2">
                <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.company_city} onChange={e => setEditValues(v => ({ ...v, company_city: e.target.value }))} placeholder="Città" />
                <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.company_zip} onChange={e => setEditValues(v => ({ ...v, company_zip: e.target.value }))} placeholder="CAP" />
              </div>
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.google_maps_url} onChange={e => setEditValues(v => ({ ...v, google_maps_url: e.target.value }))} placeholder="Google Maps URL" />
              <select
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500"
                value={editValues.prospect_status}
                onChange={e => setEditValues(v => ({ ...v, prospect_status: e.target.value }))}
              >
                <option value="">Nessuno</option>
                <option value="new">Nuovo</option>
                <option value="to_visit">Da Visitare</option>
                <option value="visited_interested">Visitato - Interessato</option>
                <option value="visited_not_interested">Visitato - Non Interessato</option>
                <option value="nurture">Da Ricontattare</option>
                <option value="disqualified">Disqualificato</option>
              </select>
              <select
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500"
                value={editValues.lifecycle_stage}
                onChange={e => setEditValues(v => ({ ...v, lifecycle_stage: e.target.value }))}
              >
                <option value="contact">Contact</option>
                <option value="lead">Lead</option>
                <option value="client">Client</option>
                <option value="prospect">Prospect</option>
              </select>
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.role} onChange={e => setEditValues(v => ({ ...v, role: e.target.value }))} placeholder="Ruolo / Role" />
              <input className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500" value={editValues.preferred_language} onChange={e => setEditValues(v => ({ ...v, preferred_language: e.target.value }))} placeholder="Lingua Preferita / Preferred Language" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowEdit(false)} className="px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Cancel</button>
                <button onClick={updateContactDetails} className="px-3 py-1.5 text-xs rounded bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-md">Save</button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            onClick={() => setShowMoreFields(prev => !prev)}
            className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            {showMoreFields ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Additional info
          </button>
          {showMoreFields && (
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <div>Created: {contact.created_at ? new Date(contact.created_at).toLocaleString() : '—'}</div>
              <div>Last activity: {contact.last_activity_at ? new Date(contact.last_activity_at).toLocaleString() : '—'}</div>
              <div>ID: {contact.id}</div>
              <div>VAT: {contact.vat_number || '—'}</div>
              <div>Rank: {contact.client_rank || '—'}</div>
            </div>
          )}
        </div>
      </div>
      <div className="order-1 lg:order-2">
        <div className="mb-4 p-3 sm:p-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-2 font-medium">Add Note</div>
          <textarea
            className="w-full bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 min-h-[90px] text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={!canEdit}
            placeholder="Aggiungi una nota..."
          />
          <button
            onClick={addNote}
            disabled={!canEdit || !note.trim()}
            className={`mt-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold transition-colors text-xs sm:text-sm shadow-md ${
              canEdit && note.trim()
                ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            Add
          </button>
        </div>
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden shadow-md">
          {timeline.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 dark:text-gray-400">No activity yet.</div>
          ) : timeline.map(entry => {
            const isWhatsapp = entry.type?.startsWith('whatsapp');
            const isAutomation = entry.channel === 'automation';
            const isNote = entry.type === 'note';
            const accentClass = entry.type === 'whatsapp_inbound'
              ? 'text-emerald-600 dark:text-emerald-400'
              : entry.type === 'whatsapp_outbound'
              ? 'text-sky-600 dark:text-sky-400'
              : entry.type === 'review_request_sent'
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-gray-700 dark:text-gray-300';
            return (
              <div key={entry.id} className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 first:border-t-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group relative">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1.5 sm:mb-1">
                  <div className="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide flex-wrap">
                    <span className={`inline-flex items-center gap-1 ${accentClass}`}>
                      <MessageCircle className="w-3 h-3" />
                      {formatTypeLabel(entry.type)}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">{formatChannelLabel(entry.channel)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">{new Date(entry.created_at).toLocaleString()}</div>
                    {isNote && canEdit && (
                      <button
                        onClick={() => deleteNote(entry.id)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400 transition-all"
                        title="Delete note"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {isWhatsapp ? (
                    <div className={`rounded-lg border px-3 py-2 text-sm ${
                      entry.type === 'whatsapp_inbound' 
                        ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100' 
                        : 'border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/30 text-sky-900 dark:text-sky-100'
                    }`}>
                      {entry.content}
                    </div>
                  ) : entry.type === 'review_request_sent' ? (
                    <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                      <FileText className="w-4 h-4" />
                      <span>{entry.content}</span>
                    </div>
                  ) : entry.type === 'note' ? (
                    entry.content
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <span>{entry.content}</span>
                      {isAutomation && (
                        <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400">automation</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ContactDetailPage;



